import * as React from 'react';
import { useFocusWithin } from 'react-aria';
import { NavigationItemModel } from '../../Header.types';
import DesktopNavigationDropdown from '../DesktopNavigationDropdown/DesktopNavigationDropdown';
import S from './DesktopNavigationLink.styles';

export type DesktopNavigationLinkProps = {
  item: NavigationItemModel;
  isActive: boolean;
  onHover: (id: string) => void;
  productsVariant?: 'A' | 'B';
  setShowDropdownOverlay: React.Dispatch<React.SetStateAction<boolean>>;
  translations: Record<string, string>;
};

const DesktopNavigationLink: React.FC<DesktopNavigationLinkProps> = ({
  item,
  isActive,
  onHover,
  productsVariant,
  setShowDropdownOverlay,
  translations,
}) => {
  const [focused, setFocused] = React.useState(false);
  const hoverTimeoutRef = React.useRef<number | null>(null);

  const navItemActive = isActive || focused;
  const hasDropdown = !!item.children?.length;

  const anchorAttributes = item.url ? { href: item.url, target: item.target ?? '_self' } : {};

  const { focusWithinProps } = useFocusWithin({
    onFocusWithinChange: (isFocusWithin) => {
      setFocused(isFocusWithin);
      setShowDropdownOverlay(hasDropdown && isFocusWithin);
    },
  });

  return (
    <S.NavItem onMouseEnter={() => handleMouseEnter(item.id)} onMouseLeave={handleMouseLeave} {...focusWithinProps}>
      <S.NavLink as={item.url ? 'a' : 'span'} {...anchorAttributes} aria-current={navItemActive} data-cta-text>
        {item.text}
      </S.NavLink>

      {!!item.children?.length && (
        <DesktopNavigationDropdown
          type={item.type}
          sectionTitle={item.title}
          items={item.children}
          visible={navItemActive}
          productsVariant={productsVariant}
          translations={translations}
        />
      )}
    </S.NavItem>
  );

  /**
   * @description Sets a slight delay before showing the dropdown and overlay - prevent flickering when the user hovers over multiple navigation items quickly
   * @param id current navigation item id
   */
  function handleMouseEnter(id: string): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    hoverTimeoutRef.current = (setTimeout(() => {
      onHover(id);
      setShowDropdownOverlay(hasDropdown);
    }, 150) as unknown) as number; // delay before showing the dropdown
  }

  /**
   * @description Clear the timeout when the mouse leaves the navigation item - allows user to hover over another item momentarily without the dropdown appearing
   */
  function handleMouseLeave(): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // If navigation item doesn't have a dropdown, clear the active id straight away
    if (!hasDropdown) {
      onHover('');
    }
  }
};

export default DesktopNavigationLink;
